import Cookies from "js-cookie";
import { createContext, type ReactNode, useMemo, useReducer } from "react";
import { OT_SHOP_TYPE_COOKIE } from "src/utils/constants";
import { createReducer, type Dispatch } from "src/utils/create-reducer";
import { localeFix } from "src/utils/genderLocale/genderLocale";

export type ShopTypeState = {
  currentShopType: string;
};

export type ShopTypeActions = { type: "SET_SHOP_TYPE"; payload: string };

export type UseShopTypeContext = {
  genderState: ShopTypeState;
  pageViewedDispatch: Dispatch<ShopTypeActions>;
};

export const ShopTypeContext = createContext<ShopTypeState>({
  currentShopType: "",
});

export const ShopTypeDispatch = createContext<
  Dispatch<ShopTypeActions> | undefined
>(undefined);

export const shopTypeReducer = createReducer<ShopTypeState, ShopTypeActions>({
  SET_SHOP_TYPE: (state, payload) => ({ ...state, currentShopType: payload }),
});

export function ShopTypeContextProvider({
  children,
  gender,
  preferredShopType,
  isCartOrFavorites,
}: {
  gender: string;
  preferredShopType: string;
  children: ReactNode;
  isCartOrFavorites: boolean;
}): JSX.Element {
  const cookieShopType = useMemo(() => Cookies.get(OT_SHOP_TYPE_COOKIE), []);
  const userDefaultShopType = useMemo(() => {
    if (!preferredShopType) {
      return;
    }

    return preferredShopType === "men" ? "men" : "women";
  }, [preferredShopType]);

  const [shopTypeState, shopTypeDispatch] = useReducer(shopTypeReducer, {
    currentShopType: gender
      ? localeFix({ gender })
      : cookieShopType || userDefaultShopType || "women",
  });

  const shopTypeContextValue = useMemo<ShopTypeState>(() => {
    if (
      !gender &&
      !isCartOrFavorites &&
      (shopTypeState.currentShopType === "kids" || cookieShopType === "kids")
    ) {
      return { currentShopType: "women" };
    }

    if (!gender && !shopTypeState.currentShopType) {
      return {
        currentShopType: cookieShopType || userDefaultShopType || "women",
      };
    }

    if (gender && gender !== shopTypeState.currentShopType) {
      return { currentShopType: localeFix({ gender }) };
    }

    return shopTypeState;
  }, [
    shopTypeState,
    cookieShopType,
    userDefaultShopType,
    isCartOrFavorites,
    gender,
  ]);

  return (
    <ShopTypeContext.Provider value={shopTypeContextValue}>
      <ShopTypeDispatch.Provider value={shopTypeDispatch}>
        {children}
      </ShopTypeDispatch.Provider>
    </ShopTypeContext.Provider>
  );
}
